import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Eye,
  Layers,
  MessageSquare,
  Mic,
  MonitorSmartphone,
  Play,
  Send,
  Square,
  ThumbsDown,
  ThumbsUp,
  Type,
  Volume2,
  X,
  Zap,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import AgentPageShell from '../../components/agent/AgentPageShell';
import PhoneMockup from '../../components/agent/PhoneMockup';
import WaveformBars from '../../components/agent/WaveformBars';

const WidgetActivePage = () => (
  <AgentPageShell
    title="Mobile Widget — Active State"
    description="Voice and text interaction surfaces once the widget is opened"
    icon={Play}
    breadcrumb={['Mobile Widget', 'Active State']}
    actions={
      <Button variant="outline" size="sm" asChild>
        <Link to="/dashboard/agent/widget-default">View default state</Link>
      </Button>
    }
  >
    {({ agent }) => <ActiveBody agent={agent} />}
  </AgentPageShell>
);

const ActiveBody = ({ agent }) => {
  const [mode, setMode] = useState('voice');
  const config = agent.published_config || agent.config || {};
  const displayName = config.display_name || agent.name;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      {/* Preview */}
      <div className="lg:col-span-5 lg:sticky lg:top-6 space-y-4">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="voice" className="gap-2">
              <Mic className="w-3.5 h-3.5" /> Voice
            </TabsTrigger>
            <TabsTrigger value="text" className="gap-2">
              <MessageSquare className="w-3.5 h-3.5" /> Text
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <PhoneMockup>
          <div className="absolute inset-0 p-5 pt-12 space-y-3 opacity-20 pointer-events-none">
            <div className="h-6 w-1/2 bg-muted rounded" />
            <div className="h-24 w-full bg-muted rounded-xl" />
            <div className="h-24 w-full bg-muted rounded-xl" />
          </div>
          {mode === 'voice' ? (
            <VoiceSheet displayName={displayName} />
          ) : (
            <TextDrawer displayName={displayName} greeting={config.greeting_message} />
          )}
        </PhoneMockup>
      </div>

      {/* Config */}
      <div className="lg:col-span-7 space-y-8">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsContent value="voice" className="mt-0">
            <Card>
              <CardContent className="p-6 space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-primary mb-1">
                    <Mic className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">
                      Voice mode
                    </span>
                  </div>
                  <h3 className="font-semibold">Audio interaction settings</h3>
                  <p className="text-sm text-muted-foreground">
                    How the widget captures speech and shows what it heard.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <ToggleRow label="Audio waveform" hint="Show the listening visualisation" defaultChecked />
                    <Separator />
                    <ToggleRow label="Noise cancellation" hint="Filter background noise while capturing" defaultChecked />
                    <Separator />
                    <ToggleRow label="Live transcription" hint="Show speech-to-text as the user speaks" defaultChecked />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Speech-to-text engine</Label>
                      <Select defaultValue="whisper">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whisper">Whisper (Arabic optimised)</SelectItem>
                          <SelectItem value="google">Google Enhanced</SelectItem>
                          <SelectItem value="azure">Azure Neural</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">
                        No speech provider is connected yet — this records the intended setting.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Language detection</Label>
                      <Tabs defaultValue="auto">
                        <TabsList className="grid grid-cols-3 w-full">
                          <TabsTrigger value="auto" className="text-[10px]">Auto</TabsTrigger>
                          <TabsTrigger value="ar" className="text-[10px]">Arabic</TabsTrigger>
                          <TabsTrigger value="en" className="text-[10px]">English</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <Label>Session timeout</Label>
                        <span className="text-xs font-mono text-primary">30s</span>
                      </div>
                      <Slider defaultValue={[30]} max={60} step={5} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="text" className="mt-0">
            <Card>
              <CardContent className="p-6 space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-primary mb-1">
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">Text mode</span>
                  </div>
                  <h3 className="font-semibold">Chat & rendering settings</h3>
                  <p className="text-sm text-muted-foreground">
                    How the chat drawer looks and behaves.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <ToggleRow label="Auto-scroll" hint="Keep new messages in view" defaultChecked />
                    <Separator />
                    <ToggleRow label="Markdown rendering" hint="Lists, bold, tables and links" defaultChecked />
                    <Separator />
                    <ToggleRow label="Typing indicator" hint={`Show "${displayName} is thinking…"`} defaultChecked />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Drawer height</Label>
                      <Select defaultValue="80">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="100">Full screen</SelectItem>
                          <SelectItem value="80">80% of screen</SelectItem>
                          <SelectItem value="60">60% of screen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Max quick-reply pills</Label>
                      <Input type="number" defaultValue={4} min={0} max={8} />
                    </div>
                    <ToggleRow label="Show agent avatar" hint="Profile bubble beside replies" defaultChecked />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Output state */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <Volume2 className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold">Agent response (output state)</h3>
          </div>
          <Card className="overflow-hidden border-primary/20 bg-primary/[0.02]">
            <div className="px-4 py-1.5 bg-primary/10 border-b border-primary/10 flex items-center justify-between">
              <span className="text-[10px] font-bold text-primary uppercase tracking-tight">
                Response preview
              </span>
              <Badge variant="secondary" className="text-[9px] h-4 bg-primary/20 text-primary border-0">
                Audio + text
              </Badge>
            </div>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-4 bg-background border border-border p-3 rounded-xl">
                <Button size="icon" className="h-10 w-10 rounded-full shrink-0">
                  <Play className="w-5 h-5 fill-current" />
                </Button>
                <WaveformBars count={20} className="flex-1 h-10" />
              </div>
              <p className="text-sm leading-relaxed p-4 bg-background/50 rounded-lg border border-dashed border-border">
                "I can help with that. Emergency towing has a 30 minute ETA, battery jump start
                15 minutes, and fuel delivery 20 minutes."
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Was this helpful?
                  </span>
                  <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
                    <ThumbsUp className="w-3 h-3" /> Yes
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
                    <ThumbsDown className="w-3 h-3" /> No
                  </Button>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-[10px] text-muted-foreground uppercase">Speed</span>
                  <div className="flex gap-1 mt-1">
                    {['0.75x', '1x', '1.5x'].map((v) => (
                      <button
                        key={v}
                        className={`text-[10px] px-2 py-0.5 rounded ${
                          v === '1x' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Behaviour */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 px-1">
            <Layers className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold">SDK behaviour & layout</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm">Transition animation</Label>
                  </div>
                  <Select defaultValue="slide-up">
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="slide-up">Slide up</SelectItem>
                      <SelectItem value="fade">Fade in</SelectItem>
                      <SelectItem value="zoom">Zoom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MonitorSmartphone className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm">Dismiss on outside tap</Label>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm">Keyboard avoidance</Label>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Type className="w-4 h-4" />
                    <Label className="text-sm">RTL layout support</Label>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
};

const VoiceSheet = ({ displayName }) => (
  <div className="absolute inset-x-0 bottom-0 bg-background/95 backdrop-blur-md border-t border-border rounded-t-3xl p-6 flex flex-col items-center">
    <div className="w-12 h-1.5 bg-muted rounded-full mb-8" />
    <p className="text-base font-medium mb-6">{displayName} is listening…</p>
    <WaveformBars count={12} className="h-20 mb-6" />
    <p className="text-sm text-muted-foreground italic mb-8 text-center px-4" dir="rtl">
      أريد طلب خدمة السحب <span className="opacity-50">[transcribing]</span>
    </p>
    <div className="flex items-center gap-6 mb-6">
      <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full border border-border">
        <X className="w-5 h-5" />
      </Button>
      <Button variant="destructive" size="icon" className="h-14 w-14 rounded-full shadow-lg">
        <Square className="w-6 h-6 fill-current" />
      </Button>
      <div className="w-11" />
    </div>
    <span className="px-3 py-1 bg-muted rounded-full text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
      Arabic detected
    </span>
  </div>
);

const TextDrawer = ({ displayName, greeting }) => (
  <div className="absolute inset-x-0 bottom-0 h-[82%] bg-background border-t border-border rounded-t-3xl flex flex-col shadow-2xl">
    <div className="p-4 border-b border-border flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center relative">
        <Bot className="w-4 h-4 text-primary" />
        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-background rounded-full" />
      </div>
      <div className="min-w-0">
        <h4 className="font-semibold text-sm truncate">{displayName}</h4>
        <p className="text-[10px] text-muted-foreground">Online</p>
      </div>
    </div>

    <div className="flex-1 overflow-y-auto p-4 space-y-3 agent-scrollbar">
      <div className="bg-muted p-3 rounded-2xl rounded-tl-none max-w-[85%] text-sm">
        {greeting || 'Hi! How can I help you today?'}
      </div>
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground p-3 rounded-2xl rounded-tr-none max-w-[85%] text-sm">
          I need roadside assistance
        </div>
      </div>
      <div className="bg-muted p-3 rounded-2xl rounded-tl-none max-w-[88%] text-sm space-y-2">
        <p>Here are your options:</p>
        <ul className="space-y-1 list-disc pl-4 text-xs">
          <li><strong>Emergency towing</strong> — 30 min</li>
          <li><strong>Battery jump start</strong> — 15 min</li>
          <li><strong>Fuel delivery</strong> — 20 min</li>
        </ul>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {['Order towing', 'Battery help', 'Talk to human'].map((pill) => (
          <span
            key={pill}
            className="px-2.5 py-1 rounded-full text-[10px] bg-secondary border border-border"
          >
            {pill}
          </span>
        ))}
      </div>
    </div>

    <div className="p-4 border-t border-border flex items-center gap-2 pb-6">
      <div className="relative flex-1">
        <div className="h-10 rounded-full bg-muted/50 flex items-center px-4 text-xs text-muted-foreground">
          Type your message…
        </div>
        <Mic className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>
      <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
        <Send className="w-4 h-4 text-primary-foreground" />
      </div>
    </div>
  </div>
);

const ToggleRow = ({ label, hint, defaultChecked }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="space-y-0.5 min-w-0">
      <Label>{label}</Label>
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
    <Switch defaultChecked={defaultChecked} />
  </div>
);

export default WidgetActivePage;
